import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { ROUTES } from '../lib/constants';
import GuideLink from '../components/GuideLink';

type Status = 'waiting' | 'verifying' | 'success' | 'error';

export default function VerifyEmail() {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { t: tGuide } = useTranslation('guide');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { verifyEmail } = useAuth();

  // location.state から email を取得（登録直後の遷移時）
  const emailFromState = (location.state as { email?: string })?.email;

  useEffect(() => {
    const token = searchParams.get('token');

    // トークンがない場合は「メール確認待ち」画面を表示
    if (!token) {
      setStatus('waiting');
      return;
    }

    // 既に処理中または成功状態の場合はスキップ（React Strict Mode対策）
    let isCancelled = false;

    // トークンがある場合は確認処理を実行
    const verify = async () => {
      try {
        const verified = await verifyEmail(token);
        if (isCancelled) return;

        if (verified) {
          setStatus('success');
          // 2秒後に配信一覧へリダイレクト
          setTimeout(() => {
            if (!isCancelled) {
              navigate(ROUTES.WATCH);
            }
          }, 2000);
        } else {
          setStatus('error');
          setError(t('verifyEmail.verificationFailed'));
        }
      } catch (err) {
        if (isCancelled) return;

        // トークンが既に使用済みの場合は成功として扱う
        // （React Strict Modeでの2回目の呼び出し対策）
        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage.includes('無効な確認トークン') || errorMessage.includes('既に確認済み')) {
          // 既にログイン状態かチェック
          if (apiClient.isAuthenticated()) {
            setStatus('success');
            setTimeout(() => {
              if (!isCancelled) {
                navigate(ROUTES.WATCH);
              }
            }, 1000);
            return;
          }
        }

        setStatus('error');
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError(t('verifyEmail.verificationFailed'));
        }
      }
    };

    verify();

    return () => {
      isCancelled = true;
    };
  }, [searchParams, verifyEmail, navigate, t]);

  // 確認メール再送信
  const handleResendEmail = async () => {
    if (!emailFromState || isResending) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      await apiClient.resendVerification(emailFromState);
      setResendSuccess(true);
    } catch (err) {
      // エラーでも特に表示しない（セキュリティ上）
      console.error('Resend failed:', err);
    } finally {
      setIsResending(false);
    }
  };

  // メール確認待ち（登録直後）
  if (status === 'waiting') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg p-8 shadow-lg border border-pink-200 w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher variant="buttons" />
          </div>

          <div className="text-center mb-8">
            <div className="inline-block bg-primary rounded-full p-3 mb-4">
              <span className="text-3xl">📧</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('verifyEmail.checkEmail')}</h1>
            <p className="text-gray-600 text-sm">
              {emailFromState ? (
                t('verifyEmail.sentTo', { email: emailFromState })
              ) : (
                t('verifyEmail.sentGeneric')
              )}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              {t('verifyEmail.instruction')}
            </p>
          </div>

          {emailFromState && (
            <div className="mb-6">
              <button
                onClick={handleResendEmail}
                disabled={isResending || resendSuccess}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {tCommon('status.loading')}
                  </span>
                ) : resendSuccess ? (
                  <span className="flex items-center justify-center gap-2 text-green-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('verifyEmail.resendSuccess')}
                  </span>
                ) : (
                  t('verifyEmail.resend')
                )}
              </button>
            </div>
          )}

          <div className="text-center">
            <Link
              to={ROUTES.LOGIN}
              className="text-sm text-primary hover:text-primary-600 transition-colors font-medium"
            >
              {t('forgotPassword.backToLogin')}
            </Link>
            <GuideLink chapter="register" className="mt-3 block text-sm font-medium text-primary hover:text-primary-600 hover:underline">
              {tGuide('links.register')}
            </GuideLink>
          </div>
        </div>
      </div>
    );
  }

  // 確認中
  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg p-8 shadow-lg border border-pink-200 w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher variant="buttons" />
          </div>

          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
            <h1 className="text-xl font-bold text-gray-900">{t('verifyEmail.verifying')}</h1>
            <p className="text-gray-600 text-sm mt-2">{t('verifyEmail.verifyingMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  // 成功
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg p-8 shadow-lg border border-pink-200 w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher variant="buttons" />
          </div>

          <div className="text-center mb-8">
            <div className="inline-block bg-green-500 rounded-full p-3 mb-4">
              <span className="text-3xl text-white">✓</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('verifyEmail.success')}</h1>
            <p className="text-gray-600 text-sm">
              {t('verifyEmail.successMessage')}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
          </div>

          <div className="mt-6 text-center">
            <Link
              to={ROUTES.WATCH}
              className="text-sm text-primary hover:text-primary-600 transition-colors font-medium"
            >
              {t('verifyEmail.goNow')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // エラー
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg p-8 shadow-lg border border-pink-200 w-full max-w-md">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="buttons" />
        </div>

        <div className="text-center mb-8">
          <div className="inline-block bg-red-500 rounded-full p-3 mb-4">
            <span className="text-3xl text-white">✕</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('verifyEmail.error')}</h1>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            {t('verifyEmail.errorMessage')}
          </p>
        </div>

        <div className="space-y-3">
          <Link
            to={ROUTES.LOGIN}
            className="block w-full py-3 px-4 bg-primary hover:bg-primary-600 text-white font-bold rounded-lg transition-all text-center"
          >
            {t('resetPassword.goToLogin')}
          </Link>
          <Link
            to={ROUTES.REGISTER}
            className="block w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-lg transition-all text-center"
          >
            {t('verifyEmail.goToRegister')}
          </Link>
        </div>
      </div>
    </div>
  );
}
